import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Injector, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Attendance, AttendanceStatusEnum, TimezoneDatePipe } from 'lib-trend-core';
import {
  CHAT_HEADER_SECTIONS,
  ChatHeaderAction,
  ChatHeaderActionId,
  ChatHeaderContext,
  ChatHeaderLayout,
  ChatHeaderPinnableId,
  ChatHeaderSection,
  chatHeaderBarActions,
  computeChatHeaderLayout,
  conversationWindowOf,
  defaultChatHeaderPins,
  isContactBlocked,
  resolveChatHeaderActions,
} from './chat-header.actions';

/** A status badge; `text`, `short` and `title` are i18n keys sharing `params`. */
interface ChatHeaderStatus {
  variant: string;
  icon: string;
  text: string;
  short: string;
  title: string;
  params?: object;
}

interface ChatHeaderMenuEntry {
  id: ChatHeaderActionId;
  section: ChatHeaderSection;
  icon: string;
  label: string;
  short?: string;
  danger?: boolean;
  pressed?: boolean;
  pinnable: boolean;
}

interface ChatHeaderMenuSection {
  section: ChatHeaderSection;
  label: string;
  entries: ChatHeaderMenuEntry[];
}

const STATUS = 'attendance.chatHeader.status.';
const PINS_STORAGE_KEY = 'trendmulti.chatHeader.pins.';

const SECTION_LABELS: Record<ChatHeaderSection, string> = {
  attendance: 'report.home.cardAttendance',
  conversation: 'attendance.chatHeader.section.conversation',
  automation: 'attendance.chatHeader.section.automation',
  contact: 'attendance.contactCard.contact',
};

/**
 * The attendance chat header: a single row whose contents follow the width of the chat card
 * itself, never the window. The user pins the actions shown in the bar; whatever does not fit
 * stays in "Mais ações".
 */
@Component({
    selector: 'attendance-chat-header-component',
    templateUrl: './chat-header.component.html',
    styleUrls: ['./chat-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatHeaderComponent extends AbstractComponent implements OnChanges, AfterViewInit, OnDestroy {

  private static nextId = 0;

  @Input() attendance: Attendance;
  @Input() aiModule = false;
  @Input() aiActive = false;
  @Input() callModule = false;
  @Input() crmIntegration = false;
  @Input() exporting = false;
  @Input() panelOpen = true;
  @Input() searchOpen = false;
  @Input() searchText = '';
  @Input() searchIndex = -1;
  @Input() searchTotal = 0;
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() action = new EventEmitter<ChatHeaderActionId>();

  @ViewChild('moreButton') moreButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') menu?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  readonly menuId = `chat-header-menu-${++ChatHeaderComponent.nextId}`;
  readonly statusId = `${this.menuId}-status`;
  readonly searchId = `${this.menuId}-search`;

  actions: ChatHeaderAction[] = [];
  pins: ChatHeaderPinnableId[] = [];
  statuses: ChatHeaderStatus[] = [];
  shownStatuses: ChatHeaderStatus[] = [];
  menuSections: ChatHeaderMenuSection[] = [];
  layout: ChatHeaderLayout = computeChatHeaderLayout(0, [], () => 0);
  menuOpen = false;
  statusOpen = false;
  copied = false;

  private storedPins: ChatHeaderPinnableId[] | null;
  private width = 0;
  private resizeObserver?: ResizeObserver;
  private copyTimer?: ReturnType<typeof setTimeout>;
  private textContext?: CanvasRenderingContext2D | null;
  private readonly textWidths = new Map<string, number>();
  private readonly timezoneDate = new TimezoneDatePipe();

  constructor(
    injector: Injector,
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
  ) {
    super(injector);
    this.storedPins = this.readPins();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attendance']?.previousValue?._id !== this.attendance?._id) {
      this.closeMenus();
    }
    this.actions = resolveChatHeaderActions(this.context());
    this.statuses = this.buildStatuses();
    this.refresh();
    if (changes['searchOpen']?.currentValue) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    // Outside the zone: only a change of what the row shows is worth a change detection.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(([entry]) => this.onResize(entry.contentRect.width));
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  override ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    clearTimeout(this.copyTimer);
    super.ngOnDestroy();
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.menuOpen = false;
    this.statusOpen = false;
  }

  get isOpen(): boolean {
    return this.attendance?.status !== AttendanceStatusEnum.CLOSED;
  }

  get deptAgent(): string {
    return [this.attendance?.department?.name, this.attendance?.user?.name].filter(Boolean).join(' · ');
  }

  get panelLabel(): string {
    return this.panelOpen ? 'attendance.chatHeader.hidePanel' : 'attendance.chatHeader.showPanel';
  }

  get panelIcon(): string {
    return this.panelOpen ? 'ri-layout-right-2-line' : 'ri-layout-right-line';
  }

  get hiddenStatusCount(): number {
    return this.statuses.length - this.shownStatuses.length;
  }

  get moreLabel(): string {
    const hidden = this.layout.hidden.length;
    if (!hidden) return this.t('attendance.chatHeader.moreActions');
    return this.t(hidden === 1 ? 'attendance.chatHeader.moreActionsHiddenOne' : 'attendance.chatHeader.moreActionsHiddenMany', { count: hidden });
  }

  get pinSummary(): string {
    const count = this.pins.length;
    const pinned = this.t(count === 1 ? 'attendance.chatHeader.pinnedOne' : 'attendance.chatHeader.pinnedMany', { count });
    const hidden = this.layout.hidden.length;
    return hidden ? `${pinned} · ${this.t('attendance.chatHeader.pinnedNoRoom', { count: hidden })}` : pinned;
  }

  get pinsAreDefault(): boolean {
    return this.pins.join() === defaultChatHeaderPins(this.actions).join();
  }

  showsLabel(action: ChatHeaderAction): boolean {
    return this.layout.labeled || (action.promoted && this.layout.promotedLabeled);
  }

  /** Labeled buttons are pills; icon-only buttons are the platform's round btn-icon. */
  buttonShape(labeled: boolean): string {
    return labeled ? 'rounded-pill' : 'btn-icon rounded-circle';
  }

  barButtonClass(action: ChatHeaderAction): string {
    const variant = action.highlight ? 'btn-primary' : action.danger ? 'btn-soft-danger' : 'btn-soft-primary';
    return `${this.buttonShape(this.showsLabel(action))} ${variant}`;
  }

  isPinned(id: ChatHeaderActionId): boolean {
    return this.pins.includes(id as ChatHeaderPinnableId);
  }

  isHidden(id: ChatHeaderActionId): boolean {
    return this.layout.hidden.some((action) => action.id === id);
  }

  isBusy(id: ChatHeaderActionId): boolean {
    return id === 'export' && this.exporting;
  }

  iconOf(entry: { id: ChatHeaderActionId; icon: string }): string {
    return this.isBusy(entry.id) ? 'spinner-border spinner-border-sm' : entry.icon;
  }

  trigger(id: ChatHeaderActionId): void {
    this.closeMenus();
    this.action.emit(id);
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.statusOpen = false;
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) {
      setTimeout(() => this.menu?.nativeElement.querySelector<HTMLElement>('.dropdown-item')?.focus());
    }
  }

  closeMenu(): void {
    this.menuOpen = false;
    this.moreButton?.nativeElement.focus();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();
    this.menuOpen = false;
    this.statusOpen = !this.statusOpen;
  }

  togglePin(id: ChatHeaderActionId): void {
    const pin = id as ChatHeaderPinnableId;
    const base = this.storedPins ?? defaultChatHeaderPins(this.actions);
    this.storedPins = base.includes(pin) ? base.filter((item) => item !== pin) : [...base, pin];
    this.writePins(this.storedPins);
    this.refresh();
  }

  restorePins(): void {
    this.storedPins = null;
    this.writePins(null);
    this.refresh();
  }

  copyProtocol(): void {
    navigator.clipboard.writeText(this.attendance.protocol).then(() => {
      this.copied = true;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => (this.copied = false), 1600);
    });
  }

  private context(): ChatHeaderContext {
    const user = this.getCurrentUserUser();
    const privileged = this.isAdmin() || this.isSupervisor();
    const attendant = this.isAttendant();
    return {
      attendance: this.attendance,
      privileged,
      canTransfer: privileged || (attendant && user?.transferAttendances === true),
      canExport: !attendant,
      canBlock: privileged || (attendant && !!user?.blockContact),
      aiModule: this.aiModule,
      aiActive: this.aiActive,
      callModule: this.callModule,
      crmIntegration: this.crmIntegration,
    };
  }

  private refresh(): void {
    const available = new Set(this.actions.map((action) => action.id));
    this.pins = (this.storedPins ?? defaultChatHeaderPins(this.actions)).filter((id) => available.has(id));
    this.applyLayout(this.computeLayout());
  }

  private computeLayout(): ChatHeaderLayout {
    return computeChatHeaderLayout(this.width, chatHeaderBarActions(this.actions, this.pins), (key) => this.measure(key));
  }

  private applyLayout(layout: ChatHeaderLayout): void {
    this.layout = layout;
    this.shownStatuses = this.statuses.slice(0, layout.statusCount);
    this.menuSections = this.buildMenuSections();
  }

  private onResize(width: number): void {
    this.width = Math.round(width);
    const next = this.computeLayout();
    if (next.key !== this.layout.key) {
      this.zone.run(() => this.applyLayout(next));
    }
  }

  /** Rendered width of a translated label, drawn as the semibold small button draws it. */
  private measure(i18nKey: string): number {
    const text = this.t(i18nKey);
    let width = this.textWidths.get(text);
    if (width === undefined) {
      width = Math.ceil(this.textCanvas()?.measureText(text).width ?? text.length * 8);
      this.textWidths.set(text, width);
    }
    return width;
  }

  private textCanvas(): CanvasRenderingContext2D | null {
    if (this.textContext === undefined) {
      this.textContext = document.createElement('canvas').getContext('2d');
      if (this.textContext) {
        this.textContext.font = `600 14px ${getComputedStyle(this.host.nativeElement).fontFamily}`;
      }
    }
    return this.textContext;
  }

  /** Every action lives in the menu; search and the panel toggle join it when the row has no room for them. */
  private buildMenuSections(): ChatHeaderMenuSection[] {
    const entries: ChatHeaderMenuEntry[] = this.actions.map((action) => ({ ...action, pinnable: true }));
    if (!this.layout.inline) {
      entries.unshift({ id: 'search', section: 'conversation', icon: 'ri-search-line', label: 'attendance.chatHeader.searchConversation', pinnable: false });
      entries.push({ id: 'panel', section: 'attendance', icon: this.panelIcon, label: this.panelLabel, pinnable: false });
    }
    return CHAT_HEADER_SECTIONS
      .map((section) => ({ section, label: SECTION_LABELS[section], entries: entries.filter((entry) => entry.section === section) }))
      .filter((group) => group.entries.length > 0);
  }

  /** Most important first: what blocks the conversation before what describes it. */
  private buildStatuses(): ChatHeaderStatus[] {
    if (!this.attendance) return [];
    return [this.closedStatus(), this.blockedStatus(), this.windowStatus(), this.pausedStatus(), this.aiStatus(), this.campaignStatus()]
      .filter((status): status is ChatHeaderStatus => !!status);
  }

  private status(variant: string, icon: string, name: string, params?: object): ChatHeaderStatus {
    return { variant, icon, text: STATUS + name, short: `${STATUS}${name}Short`, title: `${STATUS}${name}Title`, params };
  }

  private dateParams(value: Date): object {
    const zone = this.getTimezoneCompanyCurrentUser();
    return { date: this.timezoneDate.transform(value, 'DD/MM', zone), time: this.timezoneDate.transform(value, 'HH:mm', zone) };
  }

  private closedStatus(): ChatHeaderStatus | null {
    const closedAt = this.attendance.closedAt ?? this.attendance.updatedAt;
    if (this.isOpen || !closedAt) return null;
    return this.status('is-neutral', 'ri-checkbox-circle-line', 'closed', this.dateParams(closedAt));
  }

  private blockedStatus(): ChatHeaderStatus | null {
    if (!this.isOpen || !isContactBlocked(this.attendance)) return null;
    return { ...this.status('is-err', 'ri-forbid-line', 'blocked'), short: 'contact.contactList.blockedBadge', title: 'attendance.chat.contactBlockedOnChannel' };
  }

  private windowStatus(): ChatHeaderStatus | null {
    const window = this.isOpen ? conversationWindowOf(this.attendance) : null;
    if (!window) return null;
    const expired = window === 'expired';
    return this.status(expired ? 'is-err' : 'is-ok', 'ri-time-line', expired ? 'windowExpired' : 'windowOpen', this.dateParams(this.attendance.expiredAt));
  }

  private pausedStatus(): ChatHeaderStatus | null {
    if (this.attendance.status !== AttendanceStatusEnum.PAUSED) return null;
    return { ...this.status('is-warn', 'ri-pause-circle-line', 'paused'), short: `${STATUS}paused` };
  }

  private aiStatus(): ChatHeaderStatus | null {
    return this.isOpen && this.aiActive ? this.status('is-run', 'ri-robot-2-line', 'ai') : null;
  }

  /** Some payloads carry the campaign as a bare id: the badge then falls back to the generic label. */
  private campaignStatus(): ChatHeaderStatus | null {
    if (!this.attendance.campaign) return null;
    const name = this.attendance.campaign.name;
    const short = 'shared.billingDetails.campaign';
    return {
      variant: 'is-neutral',
      icon: 'ri-megaphone-line',
      text: name ? `${STATUS}campaign` : short,
      short,
      title: name ? 'attendance.chat.campaignOrigin' : 'attendance.chat.campaignOriginUnknown',
      params: { name },
    };
  }

  private pinsKey(): string {
    return PINS_STORAGE_KEY + this.getIDCurrentUser();
  }

  private readPins(): ChatHeaderPinnableId[] | null {
    try {
      const stored = JSON.parse(localStorage.getItem(this.pinsKey()) ?? 'null');
      return Array.isArray(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private writePins(pins: ChatHeaderPinnableId[] | null): void {
    try {
      if (pins) {
        localStorage.setItem(this.pinsKey(), JSON.stringify(pins));
      } else {
        localStorage.removeItem(this.pinsKey());
      }
    } catch {
      // Storage blocked or full: the choice still holds until the page reloads.
    }
  }
}
